import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, Sparkles, Star, Phone, Globe, MapPin, Bot, Award,
  BadgeCheck, ShieldCheck, Tag, Send, ArrowRight, Loader2, Zap,
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Render **bold** segments from the AI answer
export const AIAnswerText = ({ text }) => {
  if (!text) return null;
  return (
    <>
      {text.split("\n").map((line, li) => (
        <p key={li} className={li > 0 ? "mt-2" : ""}>
          {line.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
            part.startsWith("**") && part.endsWith("**") ? (
              <strong key={i} className="text-white font-semibold">{part.slice(2, -2)}</strong>
            ) : (
              <span key={i}>{part}</span>
            )
          )}
        </p>
      ))}
    </>
  );
};

const TIER_BADGES = [
  { key: "is_best_of_best", label: "Best of Best", icon: Award, cls: "bg-gradient-to-r from-[#F59E0B]/20 to-[#FBBF24]/20 text-[#FBBF24] border-[#F59E0B]/40" },
  { key: "is_featured", label: "Featured", icon: Sparkles, cls: "bg-[#7C3AED]/15 text-[#A78BFA] border-[#7C3AED]/40" },
  { key: "is_verified", label: "Verified", icon: BadgeCheck, cls: "bg-[#00E5FF]/10 text-[#00E5FF] border-[#00E5FF]/30" },
  { key: "is_claimed", label: "Claimed", icon: ShieldCheck, cls: "bg-[#10B981]/10 text-[#10B981] border-[#10B981]/30" },
];

const BusinessResultCard = ({ business, index }) => {
  const badge = TIER_BADGES.find((t) => business[t.key]);
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.06 }}
      className={`glass-card rounded-2xl p-5 border transition-all hover:border-[#7C3AED]/50 ${
        business.is_best_of_best ? "border-[#F59E0B]/40 shadow-[0_0_30px_rgba(245,158,11,0.08)]" : "border-white/10"
      }`}
      data-testid={`ai-search-result-card-${index}`}
    >
      <div className="flex items-start gap-4">
        {business.logo_url ? (
          <img src={business.logo_url} alt={business.name} className="w-14 h-14 rounded-xl object-cover bg-white/5 flex-shrink-0" />
        ) : (
          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#7C3AED]/30 to-[#00E5FF]/20 flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold text-lg">{(business.name || "?").charAt(0)}</span>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-white font-semibold font-['Outfit'] truncate">{business.name}</h3>
            {badge && (
              <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border font-medium ${badge.cls}`}>
                <badge.icon className="w-3 h-3" /> {badge.label}
              </span>
            )}
            {business.coupon_headline && (
              <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border font-medium bg-[#EC4899]/10 text-[#EC4899] border-[#EC4899]/30">
                <Tag className="w-3 h-3" /> Deal Available
              </span>
            )}
          </div>
          <p className="text-[#7C3AED] text-xs mt-0.5">{business.category_name}</p>
          <p className="text-[#94A3B8] text-sm mt-1.5 line-clamp-2">{business.short_description}</p>
          <div className="flex items-center gap-3 mt-2 flex-wrap text-xs text-[#94A3B8]">
            {(business.city || business.state) && (
              <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{[business.city, business.state].filter(Boolean).join(", ")}</span>
            )}
            {business.rating ? (
              <span className="flex items-center gap-1 text-[#F59E0B]"><Star className="w-3 h-3 fill-current" />{business.rating}{business.review_count ? ` (${business.review_count})` : ""}</span>
            ) : null}
            {business.contact_phone && (
              <a href={`tel:${business.contact_phone}`} className="flex items-center gap-1 hover:text-white transition-colors"><Phone className="w-3 h-3" />{business.contact_phone}</a>
            )}
            {business.website && (
              <a href={business.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-white transition-colors"><Globe className="w-3 h-3" />Website</a>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 mt-4">
        <Link
          to={`/business/${business.slug}`}
          className="flex-1 text-center py-2 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#6D28D9] hover:from-[#8B5CF6] hover:to-[#7C3AED] text-white text-sm font-medium transition-all"
          data-testid={`ai-search-view-listing-${index}`}
        >
          View Listing
        </Link>
        {business.is_ai_certified && (
          <Link
            to={`/business/${business.slug}`}
            className="flex-1 text-center py-2 rounded-full bg-[#00E5FF]/10 border border-[#00E5FF]/30 text-[#00E5FF] hover:bg-[#00E5FF]/20 text-sm font-medium transition-all flex items-center justify-center gap-1.5"
          >
            <Bot className="w-4 h-4" /> Chat with {business.ai_agent_name || "AI Hero"}
          </Link>
        )}
      </div>
    </motion.div>
  );
};

const FollowUpChat = ({ sessionId }) => {
  const [thread, setThread] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [thread]);

  const ask = async (e) => {
    e.preventDefault();
    const q = input.trim();
    if (!q || loading) return;
    setInput("");
    setThread((t) => [...t, { role: "user", text: q }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/v1/ai-search/followup`, { session_id: sessionId, question: q });
      setThread((t) => [...t, { role: "ai", text: res.data.answer }]);
    } catch {
      setThread((t) => [...t, { role: "ai", text: "Sorry, I couldn't answer that right now. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="glass-card rounded-2xl border border-white/10 p-5 mt-8" data-testid="ai-search-followup">
      <div className="flex items-center gap-2 mb-3">
        <Bot className="w-4 h-4 text-[#00E5FF]" />
        <span className="text-sm text-white font-medium">Need more details? Ask me anything about these businesses...</span>
      </div>
      {thread.length > 0 && (
        <div className="space-y-3 mb-4 max-h-72 overflow-y-auto pr-1">
          {thread.map((m, i) => (
            <div key={i} className={`text-sm leading-relaxed ${m.role === "user" ? "text-right" : ""}`}>
              <div className={`inline-block max-w-[85%] px-4 py-2.5 rounded-2xl ${
                m.role === "user"
                  ? "bg-[#7C3AED]/20 text-white border border-[#7C3AED]/30"
                  : "bg-white/5 text-[#CBD5E1] border border-white/10 text-left"
              }`}>
                <AIAnswerText text={m.text} />
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex items-center gap-2 text-[#94A3B8] text-sm"><Loader2 className="w-4 h-4 animate-spin text-[#00E5FF]" /> Thinking...</div>
          )}
          <div ref={endRef} />
        </div>
      )}
      <form onSubmit={ask} className="relative">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder='Try "Do they work weekends?" or "What services do they offer?"'
          className="w-full pl-4 pr-12 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder:text-[#64748B] focus:border-[#00E5FF] focus:outline-none text-sm transition-all"
          data-testid="ai-search-followup-input"
        />
        <button
          type="submit"
          disabled={loading || !input.trim()}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-lg bg-[#00E5FF]/15 text-[#00E5FF] hover:bg-[#00E5FF]/25 disabled:opacity-40 transition-all"
          data-testid="ai-search-followup-send-btn"
        >
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
};

const LoadingState = ({ query }) => (
  <div className="glass-card rounded-2xl border border-white/10 p-8 text-center" data-testid="ai-search-loading">
    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-[#7C3AED]/30 to-[#00E5FF]/20 mb-4">
      <Zap className="w-7 h-7 text-[#00E5FF] animate-pulse" />
    </div>
    <p className="text-white font-medium">LocalBest AI is searching 33,000+ businesses...</p>
    <p className="text-[#94A3B8] text-sm mt-1">"{query}"</p>
    <div className="flex justify-center gap-1.5 mt-4">
      {[0, 1, 2].map((i) => (
        <span key={i} className="w-2 h-2 rounded-full bg-[#7C3AED] animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />
      ))}
    </div>
  </div>
);

export default function AISearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const q = searchParams.get("q") || "";
  const [inputValue, setInputValue] = useState(q);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const sessionRef = useRef(`aisearch-${Math.random().toString(36).slice(2, 14)}`);

  const runSearch = useCallback(async (query) => {
    if (!query || query.trim().length < 2) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await axios.post(`${API}/v1/ai-search`, { query: query.trim(), session_id: sessionRef.current });
      setResult(res.data);
    } catch (e) {
      setError("Something went wrong with the AI search. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = q ? `${q} — LocalBest AI Search` : "AI Search — LocalBest.ai";
    if (q) {
      setInputValue(q);
      runSearch(q);
    }
  }, [q, runSearch]);

  const submit = (e) => {
    e.preventDefault();
    const v = inputValue.trim();
    if (!v) return;
    sessionRef.current = `aisearch-${Math.random().toString(36).slice(2, 14)}`;
    setSearchParams({ q: v });
  };

  const hasBusinesses = result?.businesses?.length > 0;

  return (
    <div className="min-h-screen bg-[#020817] pt-28 pb-20">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        {/* Header + search bar */}
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-[#7C3AED]/20 to-[#00E5FF]/20 border border-[#7C3AED]/30 mb-4">
            <Sparkles className="w-3.5 h-3.5 text-[#00E5FF]" />
            <span className="text-xs text-[#00E5FF] font-medium">LocalBest AI Search</span>
          </div>
          <form onSubmit={submit} className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Ask LocalBest AI... 'Find me a roofer in Chicago'"
              className="w-full pl-12 pr-28 py-4 rounded-xl bg-white/10 border border-white/20 text-white placeholder:text-[#64748B] focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/20 focus:outline-none transition-all"
              data-testid="ai-search-page-input"
            />
            <button
              type="submit"
              className="absolute right-2 top-1/2 -translate-y-1/2 px-4 py-2 rounded-lg bg-gradient-to-r from-[#7C3AED] to-[#6D28D9] hover:from-[#8B5CF6] hover:to-[#7C3AED] text-white text-sm font-medium transition-all flex items-center gap-1.5"
              data-testid="ai-search-page-submit-btn"
            >
              <Sparkles className="w-4 h-4" /> Ask AI
            </button>
          </form>
        </div>

        {loading && <LoadingState query={q} />}

        {error && (
          <div className="glass-card rounded-2xl border border-red-500/30 p-6 text-center" data-testid="ai-search-error">
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {!loading && !q && !result && (
          <div className="text-center text-[#94A3B8] py-12">
            <p className="text-lg text-white font-medium mb-2">What are you looking for?</p>
            <p className="text-sm">Try natural language — "I need a roofer in Naperville" or "best pizza in Denver"</p>
            <div className="flex flex-wrap justify-center gap-2 mt-6">
              {["I need a roofer in Naperville", "storm damage repair", "home loan in Miami", "best restaurant near me"].map((s) => (
                <button
                  key={s}
                  onClick={() => setSearchParams({ q: s })}
                  className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm text-[#CBD5E1] hover:border-[#7C3AED]/50 hover:text-white transition-all"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        <AnimatePresence>
          {!loading && result && (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
              {/* AI answer panel */}
              <div
                className={`glass-card rounded-2xl p-6 border ${result.had_best_of_best ? "border-[#F59E0B]/30" : "border-[#7C3AED]/30"}`}
                data-testid="ai-search-answer"
              >
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#00E5FF] flex items-center justify-center flex-shrink-0">
                    <Zap className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-sm font-semibold text-white font-['Outfit']">LocalBest AI</span>
                  {result.parsed?.city && (
                    <span className="text-xs text-[#94A3B8] flex items-center gap-1"><MapPin className="w-3 h-3" />{[result.parsed.city, result.parsed.state].filter(Boolean).join(", ")}</span>
                  )}
                </div>
                <div className="text-[#CBD5E1] leading-relaxed text-[15px]">
                  <AIAnswerText text={result.answer} />
                </div>
                {result.location_fallback && (
                  <p className="text-xs text-[#F59E0B] mt-3">No exact matches in {result.parsed?.city} — showing the closest results in the region.</p>
                )}
              </div>

              {/* Result cards */}
              {hasBusinesses ? (
                <div className="space-y-4 mt-6" data-testid="ai-search-results-list">
                  {result.businesses.map((b, i) => (
                    <BusinessResultCard key={b.id || b.slug || i} business={b} index={i} />
                  ))}
                </div>
              ) : (
                <div className="glass-card rounded-2xl border border-[#10B981]/30 p-8 text-center mt-6" data-testid="ai-search-no-results">
                  <p className="text-white font-semibold text-lg mb-2">
                    This category is wide open{result.parsed?.city ? ` in ${result.parsed.city}` : ""}.
                  </p>
                  <p className="text-[#94A3B8] text-sm mb-5">Own a business in this space? The Best of Best spot is still available.</p>
                  <Link
                    to="/submit"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-[#10B981] to-[#059669] text-white font-medium hover:opacity-90 transition-all"
                    data-testid="ai-search-claim-spot-btn"
                  >
                    Claim your spot <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              )}

              {/* Follow-up chat */}
              {hasBusinesses && <FollowUpChat sessionId={result.session_id} />}

              {/* Secondary: browse directory */}
              <div className="text-center mt-8">
                <button
                  onClick={() => navigate("/directory")}
                  className="text-sm text-[#94A3B8] hover:text-white transition-colors"
                  data-testid="ai-search-browse-directory-link"
                >
                  Prefer browsing by category? Explore the full directory →
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
